import React, { useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useTable, useFlexLayout } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';

import { createExpanderColumnDefinition } from './columns/expander-column';
import { ForecastingGridNumericColumn } from './columns/numeric-column';
import { ProductColumn } from './columns/product-column';
import { QuantityColumn } from './columns/quantity-column';
import tableStyle from './forecasting-grid-table.module.less';
import style from './forecasting-grid.module.less';
import messages from './messages';
import { Index } from './velocity-chart';
import { useForecastingData } from '../../hooks/use-forecasting-data';

const createColumns = (updateData, showUpsell, isLoading = false) => [
  {
    Header: <FormattedMessage {...messages.productNameColumnHeader} />,
    accessor: 'product',
    width: 4,
    className: 'product-column',
    Cell: ({ value }) => <ProductColumn product={value} />,
  },
  {
    Header: <FormattedMessage {...messages.currentOnHandColumnHeader} />,
    accessor: 'currentOnHand',
    width: 1,
    className: 'numeric-column',
    Cell: ({ value }) => <ForecastingGridNumericColumn showUpsell={showUpsell} value={value} />,
  },
  {
    Header: <FormattedMessage {...messages.reorderThresholdColumnHeader} />,
    accessor: 'reorderThreshold',
    width: 1,
    className: 'numeric-column',
    Cell: ({ value }) => <ForecastingGridNumericColumn showUpsell={showUpsell} value={value} />,
  },
  {
    Header: <FormattedMessage {...messages.avgOrdersColumnHeader} />,
    accessor: 'avgOrders',
    width: 1,
    className: 'numeric-column',
    Cell: ({ value }) => <ForecastingGridNumericColumn showUpsell={showUpsell} value={value} />,
  },
  {
    Header: <FormattedMessage {...messages.suggestedReorderColumnHeader} />,
    accessor: 'suggestedReorder',
    width: 1,
    className: 'numeric-column',
    Cell: ({ value }) => (
      <ForecastingGridNumericColumn
        formatter={(val) => (val > 0 ? `+${val}` : val)}
        showUpsell={showUpsell}
        upsellPlaceholder='-'
        value={value}
      />
    ),
  },
  {
    Header: <FormattedMessage {...messages.quantityColumnHeader} />,
    accessor: 'quantity',
    width: 1,
    className: 'numeric-column',
    Cell: (props) => <QuantityColumn {...props} isLoading={isLoading} showUpsell={showUpsell} updateData={updateData} />,
  },
];

/**
 * ForecastingGridTable component that displays inventory forecasting data in a table
 * Uses the ForecastingDataContext to access data and settings
 * @param {object} props - Component props
 * @param {object} props.expandedRows - Object mapping row IDs to their expanded state
 * @param {(rowId: string, isExpanded: boolean) => void} props.handleRowExpand - Function to handle row expansion
 * @returns {React.ReactElement} The rendered ForecastingGridTable component
 */
export const ForecastingGridTable = ({ expandedRows, handleRowExpand }) => {
  const { data, isLoading, apiLoading, error, updateData, showUpsell } = useForecastingData();

  const transformedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      product: {
        imageUrl: item.imageUrl,
        productName: item.productName,
        sku: item.sku,
      },
    }));
  }, [data]);

  const columns = useMemo(() => {
    const baseColumns = createColumns(updateData, showUpsell, apiLoading);

    return [...baseColumns, createExpanderColumnDefinition(expandedRows, handleRowExpand)];
  }, [updateData, showUpsell, apiLoading, expandedRows, handleRowExpand]);

  const renderSubRow = useCallback(
    (row) => {
      const isExpanded = expandedRows[row.id] || false;
      return isExpanded ? (
        <Index hasSufficientHistoricalData={row.original.hasSufficientHistoricalData} isExpanded={isExpanded} productId={row.original.id} />
      ) : null;
    },
    [expandedRows],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: transformedData,
    },
    useFlexLayout,
  );

  const getBodyRowClassName = useCallback(
    (row) => {
      const isExpanded = expandedRows[row.id] || false;
      return isExpanded ? style['row--expanded'] : '';
    },
    [expandedRows],
  );

  if (error) {
    return (
      <div className={style['grid-error']}>
        <FormattedMessage {...messages.errorLoadingData} />
      </div>
    );
  }

  if (transformedData.length === 0 && !isLoading) {
    return (
      <div className={style['grid-empty']}>
        <FormattedMessage {...messages.noItemsToReorder} />
      </div>
    );
  }

  const emptyRow = (
    <div className={style['grid-empty']}>
      <FormattedMessage {...messages.noItemsToReorder} />
    </div>
  );

  return (
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
    <div className={classnames(style.grid, tableStyle['forecasting-grid'])} role='region'>
      <div className={style['table-container']}>
        <ReactTableWrapper
          className={classnames(style.table, tableStyle.table)}
          emptyRow={transformedData.length === 0 && !isLoading ? emptyRow : undefined}
          getBodyRowClassName={getBodyRowClassName}
          getTableBodyProps={getTableBodyProps}
          getTableProps={getTableProps}
          headerGroups={headerGroups}
          headerRowClassName={style['header-row']}
          isLoading={isLoading || apiLoading}
          prepareRow={prepareRow}
          renderSubRow={renderSubRow}
          rows={rows}
        />
      </div>
    </div>
  );
};
